<!-- AppDownload.svelte — public-to-all logged-in portal section for installers. -->
<script lang="ts">
  import { onMount } from "svelte";
  import { Download } from "lucide";
  import { Icon, Button } from "@price-check/ui";
  import Card from "../lib/ui/Card.svelte";

  type OS = "Windows" | "macOS" | "Linux";
  type AppDownload = { os: OS; version: string; file: string };

  const SOURCES: { os: OS; manifest: string; prefer: string[] }[] = [
    { os: "Windows", manifest: "latest.yml", prefer: [".exe"] },
    { os: "macOS", manifest: "latest-mac.yml", prefer: [".dmg", ".zip"] },
    { os: "Linux", manifest: "latest-linux.yml", prefer: [".appimage", ".deb"] },
  ];

  let downloads = $state<AppDownload[]>([]);
  let relStatus = $state<"loading" | "ready" | "none">("loading");

  let ext = $state<{ version: string; file: string } | null>(null);
  let extStatus = $state<"loading" | "ready" | "none">("loading");

  const extSteps = [
    "Download the zip below and unzip it to a folder you'll keep (deleting it removes the extension).",
    "Open chrome://extensions in Chrome (or any Chromium browser).",
    'Turn on "Developer mode" (top-right toggle).',
    'Click "Load unpacked" and select the unzipped folder.',
    "Keep Chrome open — the desktop app links to it automatically.",
  ];

  function pickInstaller(text: string, prefer: string[]): { version: string; file: string } | null {
    const version = text.match(/^version:\s*(.+)$/m)?.[1]?.trim();
    if (!version) return null;
    const urls = [...text.matchAll(/^\s*-\s*url:\s*(.+)$/gm)]
      .map((m) => (m[1] ?? "").trim())
      .filter((u) => u.length > 0);
    const path = text.match(/^path:\s*(.+)$/m)?.[1]?.trim();
    let file: string | undefined;
    for (const ext of prefer) {
      file = urls.find((u) => u.toLowerCase().endsWith(ext));
      if (file) break;
    }
    file = file ?? path ?? urls[0];
    return file ? { version, file } : null;
  }

  onMount(async () => {
    const found = await Promise.all(
      SOURCES.map(async (s): Promise<AppDownload | null> => {
        try {
          const r = await fetch(`/releases/${s.manifest}`, { cache: "no-store" });
          if (!r.ok) return null;
          const p = pickInstaller(await r.text(), s.prefer);
          return p ? { os: s.os, version: p.version, file: p.file } : null;
        } catch {
          return null;
        }
      }),
    );
    downloads = found.filter((d): d is AppDownload => d !== null);
    relStatus = downloads.length > 0 ? "ready" : "none";

    try {
      const r = await fetch("/releases/extension.yml", { cache: "no-store" });
      const text = r.ok ? await r.text() : "";
      const version = text.match(/^version:\s*(.+)$/m)?.[1]?.trim();
      const file = text.match(/^path:\s*(.+)$/m)?.[1]?.trim();
      ext = version && file ? { version, file } : null;
    } catch {
      ext = null;
    }
    extStatus = ext ? "ready" : "none";
  });
</script>

<div class="flex max-w-[640px] flex-col gap-5">
  <Card title="Desktop app" subtitle="Install Pretaro. Windows & Linux auto-update; macOS is a manual download.">
    {#if relStatus === "loading"}
      <div class="text-sm text-muted">Checking for downloads…</div>
    {:else if relStatus === "none"}
      <div class="text-sm text-muted">No downloads published yet.</div>
    {:else}
      <div class="flex flex-col gap-3">
        {#each downloads as d (d.os)}
          <div class="flex flex-col gap-2">
            <div class="flex flex-wrap items-center justify-between gap-4">
              <div class="min-w-0">
                <div class="text-sm font-semibold text-ink">{d.os}</div>
                <div class="text-sm text-muted">Version {d.version}</div>
              </div>
              <Button href={`/releases/${encodeURIComponent(d.file)}`} size="sm">
                <Icon icon={Download} size={14} />
                Download
              </Button>
            </div>
            {#if d.os === "macOS"}
              <div class="text-xs text-muted">
                If macOS says it's "damaged", open Terminal once and run:
                <code class="mt-1 block rounded bg-surface-2 px-2 py-1 font-mono text-[11px] text-ink"
                  >xattr -cr "/Applications/Pretaro.app"</code>
              </div>
            {:else if d.os === "Windows"}
              <div class="text-xs text-muted">
                Windows may warn "Windows protected your PC" — click <strong>More info → Run anyway</strong>.
                It updates automatically after install.
              </div>
            {/if}
          </div>
        {/each}
      </div>
    {/if}
  </Card>

  <Card title="Browser extension" subtitle="Install in Chrome via Developer mode (Load unpacked). Update by replacing the folder + reloading.">
    {#if extStatus === "loading"}
      <div class="text-sm text-muted">Checking for the extension…</div>
    {:else if extStatus === "none"}
      <div class="text-sm text-muted">No extension published yet.</div>
    {:else if ext}
      <div class="flex flex-col gap-4">
        <div class="flex flex-wrap items-center justify-between gap-4">
          <div class="min-w-0">
            <div class="text-sm font-semibold text-ink">Pretaro extension</div>
            <div class="text-sm text-muted">Version {ext.version}</div>
          </div>
          <Button href={`/releases/${encodeURIComponent(ext.file)}`} size="sm">
            <Icon icon={Download} size={14} />
            Download zip
          </Button>
        </div>
        <ol class="flex list-decimal flex-col gap-1.5 pl-5 text-sm text-muted">
          {#each extSteps as step (step)}
            <li>{step}</li>
          {/each}
        </ol>
      </div>
    {/if}
  </Card>
</div>
